import React from 'react'
import { useForm } from 'react-hook-form'
import { useEthPrice } from '@components/Web3/useEthPrice'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, MotionBox, Stack, Text, TextField } from '@ui'
import { env } from 'utils'
import { z } from 'zod'

import { trackTipAmount } from './tipAnalytics'
import type { TipOption } from './types'

const staticEthMode = env.VITE_TIPS_IN_ETH

const BUTTON_HEIGHT = '32px'
const MODAL_WIDTH = '200px'

const createCustomTipSchema = (isEthMode: boolean) =>
    z.object({
        amount: z
            .string()
            .refine(
                (val) => (isEthMode ? /^\d*\.?\d{0,18}$/.test(val) : /^\d*\.?\d{0,2}$/.test(val)),
                {
                    message: isEthMode
                        ? 'Please enter a valid amount (max 18 decimal places)'
                        : 'Please enter a valid amount (max 2 decimal places)',
                },
            )
            .refine(
                (val) => {
                    const num = parseFloat(val)
                    if (!val || isNaN(num)) {
                        return true
                    }
                    if (isEthMode) {
                        return num >= 0.0001 // 0.0001 minimum for ETH mode
                    } else {
                        return num >= 0.25 // $0.25 minimum for USD mode
                    }
                },
                isEthMode ? 'Amount must be greater than or equal to 0.0001' : 'Min tip is $0.25',
            ),
    })

export function TipMenu(props: {
    tipValue: TipOption | undefined
    setTipValue: (option: TipOption | undefined) => void
    inSheet?: boolean
    confirmRenderer: React.ReactNode
}) {
    const { tipValue, setTipValue, confirmRenderer, inSheet } = props

    const { data: ethPrice, isFallbackMode } = useEthPrice({
        enabled: !staticEthMode,
    })

    const IS_DYNAMIC_ETH_MODE = staticEthMode || isFallbackMode

    // Dynamic tip options based on current mode
    const tipOptions: TipOption[] = IS_DYNAMIC_ETH_MODE
        ? [
              {
                  amountInCents: 0, // Will be calculated based on current ETH price
                  ethAmount: 0.0003,
                  label: '0.0003 ETH',
              },
              {
                  amountInCents: 0, // Will be calculated based on current ETH price
                  ethAmount: 0.0015,
                  label: '0.0015 ETH',
              },
              {
                  amountInCents: 0, // Will be calculated based on current ETH price
                  ethAmount: 0.003,
                  label: '0.003 ETH',
              },
          ]
        : [
              {
                  amountInCents: 100,
                  label: '$1',
              },
              {
                  amountInCents: 500,
                  label: '$5',
              },
              {
                  amountInCents: 1_000,
                  label: '$10',
              },
          ]

    const customTipSchema = createCustomTipSchema(IS_DYNAMIC_ETH_MODE)
    type CustomTipForm = z.infer<typeof customTipSchema>

    const {
        register,
        formState: { errors },
        watch,
        handleSubmit,
    } = useForm<CustomTipForm>({
        resolver: zodResolver(customTipSchema),
        mode: 'onBlur',
    })

    const amount = watch('amount')

    const handleTip = (tip: TipOption | CustomTipForm) => {
        let option: TipOption
        if ('amount' in tip) {
            // Handle custom tip
            const numAmount = parseFloat(tip.amount)
            if (isNaN(numAmount) || numAmount <= 0) {
                return
            }

            if (IS_DYNAMIC_ETH_MODE) {
                const ethPriceNum = Number(ethPrice)
                if (!isNaN(ethPriceNum) && !isNaN(numAmount) && ethPrice) {
                    option = {
                        amountInCents: Math.round(numAmount * ethPriceNum * 100),
                        ethAmount: numAmount,
                        label: `${numAmount} ETH`,
                    }
                } else if (isFallbackMode) {
                    // In fallback mode, we can't convert to USD, so use a default value
                    option = {
                        amountInCents: 0, // Will be ignored in fallback mode
                        ethAmount: numAmount,
                        label: `${numAmount} ETH`,
                    }
                } else {
                    return
                }
            } else {
                option = {
                    amountInCents: Math.round(numAmount * 100),
                    label: `$${Number(numAmount).toFixed(2)}`,
                }
            }
        } else {
            // Handle preset tip
            if (IS_DYNAMIC_ETH_MODE && ethPrice && tip.ethAmount) {
                // Update amountInCents based on current ETH price
                const ethPriceNum = Number(ethPrice)
                const ethAmountNum = Number(tip.ethAmount)
                if (!isNaN(ethPriceNum) && !isNaN(ethAmountNum)) {
                    const cents = ethAmountNum * ethPriceNum * 100
                    tip.amountInCents = Math.round(cents)
                }
            }
            option = tip
        }

        console.log('handleTip:', option)
        trackTipAmount(option.amountInCents, option.ethAmount)
        // Use setTimeout to ensure this runs after the current event loop
        setTimeout(() => {
            setTipValue(option)
        }, 0)
    }

    if (!env.VITE_TIPS_ENABLED) {
        return null
    }

    return (
        <MotionBox
            padding={!inSheet}
            border={!inSheet}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            gap="md"
            background="level1"
            borderRadius="sm"
            boxShadow="card"
            pointerEvents="auto"
            style={{
                width: inSheet ? '100%' : MODAL_WIDTH,
                ...(!inSheet && {
                    position: 'relative',
                    right: '0',
                }),
            }}
        >
            {tipValue !== undefined ? (
                <Box pointerEvents="auto">{confirmRenderer}</Box>
            ) : (
                <>
                    <Text strong textAlign="center">
                        Tip Amount
                    </Text>
                    {isFallbackMode && (
                        <Text size="xs" color="accent" textAlign="center">
                            Using ETH amounts due to price service unavailability
                        </Text>
                    )}
                    <Stack noValidate gap="sm" as="form" onSubmit={handleSubmit(handleTip)}>
                        {tipOptions.map((tip) => (
                            <Button
                                key={tip.ethAmount || tip.amountInCents}
                                rounded="full"
                                size="button_sm"
                                style={{ height: BUTTON_HEIGHT }}
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault()
                                    handleTip(tip)
                                }}
                            >
                                {tip.label}
                            </Button>
                        ))}
                        <Box position="relative">
                            <Box
                                background="level3"
                                rounded="full"
                                padding="sm"
                                style={{
                                    cursor: 'text',
                                    height: BUTTON_HEIGHT,
                                    border: errors.amount
                                        ? '1px solid var(--colors-negative)'
                                        : 'none',
                                }}
                                onClick={(e) => {
                                    const input = e.currentTarget.querySelector('input')
                                    if (input) {
                                        input.focus()
                                    }
                                }}
                            >
                                <Stack
                                    horizontal
                                    alignItems="center"
                                    gap="md"
                                    height="100%"
                                    paddingX="xs"
                                >
                                    <TextField
                                        {...register('amount')}
                                        placeholder="Amount"
                                        background="none"
                                        style={{
                                            padding: 0,
                                            height: '20px',
                                            flex: 1,
                                            fontSize: '14px',
                                            width: '75%',
                                            border: errors.amount
                                                ? '1px solid var(--colors-negative)'
                                                : 'none',
                                            borderRadius: errors.amount ? '4px' : '0',
                                        }}
                                        type="number"
                                        step={IS_DYNAMIC_ETH_MODE ? '0.000000000000000001' : '0.01'}
                                        min={IS_DYNAMIC_ETH_MODE ? '0.0001' : '0.25'}
                                        onInput={(e) => {
                                            const value = e.currentTarget.value
                                            if (value.includes('.')) {
                                                const [, decimals] = value.split('.')
                                                if (
                                                    decimals &&
                                                    decimals.length > (IS_DYNAMIC_ETH_MODE ? 18 : 2)
                                                ) {
                                                    e.currentTarget.value = Number(value).toFixed(
                                                        IS_DYNAMIC_ETH_MODE ? 18 : 2,
                                                    )
                                                }
                                            }
                                        }}
                                    />
                                    <Text
                                        fontSize="sm"
                                        style={{ minWidth: '35px', textAlign: 'left' }}
                                    >
                                        {IS_DYNAMIC_ETH_MODE ? 'ETH' : 'USD'}
                                    </Text>
                                </Stack>
                            </Box>
                            {errors.amount && (
                                <Text color="negative" fontSize="xs" paddingTop="sm">
                                    {errors.amount.message}
                                </Text>
                            )}
                        </Box>
                        <Button
                            tone="cta1"
                            rounded="full"
                            size="button_sm"
                            type="submit"
                            style={{ height: BUTTON_HEIGHT }}
                            disabled={!amount || !!errors.amount}
                        >
                            Tip
                        </Button>
                    </Stack>
                </>
            )}
        </MotionBox>
    )
}
